import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminToastService,
} from '../../admin';
import {
  formatMergeFieldValue,
  LEAD_MERGE_FIELD_LABELS,
  mergeConflictChoicePrompt,
  type LeadMergeArea,
  type LeadMergeExecuteResult,
  type LeadMergePreview,
  type LeadMergePreviewLead,
} from '../../models/lead-merge.model';
import { LeadsService } from '../../services/leads.service';
import { TenantLeadsService } from '../../services/tenant-leads.service';

type MergeWizardStep = 1 | 2 | 3 | 4;

@Component({
  selector: 'app-lead-merge-modal',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    AdminModalComponent,
    AdminButtonComponent,
    AdminEmptyStateComponent,
  ],
  templateUrl: './lead-merge-modal.component.html',
  styleUrl: './lead-merge-modal.component.scss',
})
export class LeadMergeModalComponent implements OnInit {
  private readonly leadsService = inject(LeadsService);
  private readonly tenantLeadsService = inject(TenantLeadsService);
  private readonly toast = inject(AdminToastService);

  readonly area = input.required<LeadMergeArea>();
  readonly leadIds = input.required<string[]>();
  readonly closed = output<void>();
  readonly merged = output<LeadMergeExecuteResult>();

  readonly loading = signal(true);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly preview = signal<LeadMergePreview | null>(null);
  readonly masterLeadId = signal('');
  readonly fieldSelections = signal<Record<string, string>>({});
  readonly confirmed = signal(false);
  readonly showAutoResolved = signal(false);
  readonly step = signal<MergeWizardStep>(1);
  private clientMergeId = '';

  readonly blockers = computed(() => this.preview()?.blockers ?? []);
  readonly warnings = computed(() => this.preview()?.warnings ?? []);
  readonly conflicts = computed(() => this.preview()?.conflicts ?? []);
  readonly autoResolved = computed(() => this.preview()?.autoResolved ?? []);

  readonly masterLead = computed(() => {
    const id = this.masterLeadId();
    return this.preview()?.leads.find((lead) => lead._id === id) ?? null;
  });

  readonly sourceLeadCount = computed(() => {
    const data = this.preview();
    if (!data) return 0;
    return Math.max(0, data.leads.length - 1);
  });

  readonly relatedRecordsTotal = computed(() => {
    const counts = this.preview()?.relatedCountsTotal;
    if (!counts) return 0;
    return (
      counts.notes +
      counts.activities +
      counts.tasks +
      counts.communications +
      counts.notifications +
      counts.applicants
    );
  });

  readonly conflictsResolved = computed(() => {
    const data = this.preview();
    if (!data) return false;
    return data.conflicts.every((conflict) => Boolean(this.fieldSelections()[conflict.field]));
  });

  readonly canContinue = computed(() => {
    if (this.loading() || this.submitting() || !this.preview()) return false;
    if (this.blockers().length > 0) return false;
    const current = this.step();
    if (current === 1) return Boolean(this.masterLeadId());
    if (current === 2) return this.conflictsResolved();
    if (current === 3) return true;
    return false;
  });

  readonly canSubmit = computed(() => {
    const data = this.preview();
    if (!data || this.submitting() || this.blockers().length > 0 || !this.confirmed()) {
      return false;
    }
    if (!this.masterLeadId()) return false;
    return this.conflictsResolved();
  });

  readonly stepTitle = computed(() => {
    switch (this.step()) {
      case 1:
        return 'Step 1 — Choose Master Lead';
      case 2:
        return 'Step 2 — Review Conflicting Information';
      case 3:
        return 'Step 3 — Review Related Data';
      case 4:
        return 'Step 4 — Confirm Merge';
    }
  });

  ngOnInit(): void {
    this.loadPreview();
  }

  close(): void {
    this.closed.emit();
  }

  loadPreview(): void {
    this.loading.set(true);
    this.error.set(null);
    this.confirmed.set(false);
    this.showAutoResolved.set(false);
    this.step.set(1);
    this.clientMergeId = '';

    const request =
      this.area() === 'tenant'
        ? this.tenantLeadsService.previewMerge(this.leadIds())
        : this.leadsService.previewMerge(this.leadIds());

    request.subscribe({
      next: (data) => {
        this.preview.set(data);
        this.masterLeadId.set(data.defaultMasterLeadId);
        const selections: Record<string, string> = {};
        for (const conflict of data.conflicts) {
          selections[conflict.field] = data.defaultMasterLeadId;
        }
        this.fieldSelections.set(selections);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.message ?? 'Could not load merge preview.');
        this.loading.set(false);
      },
    });
  }

  setMaster(leadId: string): void {
    this.masterLeadId.set(leadId);
  }

  setFieldSelection(field: string, leadId: string): void {
    this.fieldSelections.update((current) => ({ ...current, [field]: leadId }));
  }

  fieldLabel(field: string): string {
    return LEAD_MERGE_FIELD_LABELS[field] ?? field;
  }

  conflictPrompt(field: string): string {
    return mergeConflictChoicePrompt(field);
  }

  displayValue(field: string, value: string | number | null): string {
    return formatMergeFieldValue(field, value, this.preview()?.leads ?? []);
  }

  leadLabel(leadId: string): string {
    const lead = this.preview()?.leads.find((item) => item._id === leadId);
    if (!lead) return leadId;
    return `${lead.fullName} (${lead.phoneNumber})`;
  }

  isMaster(lead: LeadMergePreviewLead): boolean {
    return lead._id === this.masterLeadId();
  }

  goBack(): void {
    const current = this.step();
    if (current > 1) {
      this.step.set((current - 1) as MergeWizardStep);
      this.error.set(null);
    }
  }

  goContinue(): void {
    if (!this.canContinue()) return;
    const current = this.step();
    if (current < 4) {
      this.step.set((current + 1) as MergeWizardStep);
      this.error.set(null);
    }
  }

  confirmMerge(): void {
    const data = this.preview();
    if (!data || !this.canSubmit()) return;

    if (!this.clientMergeId) {
      this.clientMergeId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `merge-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    const masterLeadId = this.masterLeadId();
    const sourceLeadIds = data.leads
      .map((lead) => lead._id)
      .filter((id) => id !== masterLeadId);

    const expectedUpdatedAt: Record<string, string> = {};
    for (const lead of data.leads) {
      if (lead.updatedAt) expectedUpdatedAt[lead._id] = lead.updatedAt;
    }

    this.submitting.set(true);
    this.error.set(null);

    const payload = {
      masterLeadId,
      sourceLeadIds,
      fieldSelections: this.fieldSelections(),
      expectedUpdatedAt,
      clientMergeId: this.clientMergeId,
    };

    const request =
      this.area() === 'tenant'
        ? this.tenantLeadsService.executeMerge(payload)
        : this.leadsService.executeMerge(payload);

    request.subscribe({
      next: (result) => {
        this.submitting.set(false);
        this.toast.success(
          result.idempotentReplay
            ? 'Merge already applied.'
            : `Merged ${result.mergedSourceIds.length} lead${result.mergedSourceIds.length === 1 ? '' : 's'} into master.`,
        );
        this.merged.emit(result);
        this.close();
      },
      error: (err) => {
        this.submitting.set(false);
        this.error.set(err?.error?.message ?? 'Could not merge leads.');
      },
    });
  }
}
